/**
 * One-Time Benefits Tab
 * 
 * Manages one-time benefits like company trips, long service awards, etc.
 * Maps to IRAS Appendix 8A section d4 (Other Benefits).
 * 
 * Design System: Admin Theme (Red)
 */

import React, { useState, useMemo } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Gift,
  Plane,
  Award,
  DollarSign,
  Calendar,
  User,
  X,
  Save,
  RefreshCw,
} from 'lucide-react';
import { FilterButton } from '../../../../components/UI/FilterButton';
import {
  useOneTimeBenefits,
  useCreateOneTimeBenefit,
  useUpdateOneTimeBenefit,
  useDeleteOneTimeBenefit,
  BENEFIT_TYPE_OPTIONS,
  ALL_STAFF_CONDITIONAL_TYPES,
} from '../../../../hooks/useBIKData';
import Portal from '../../../../components/UI/Portal';
import IconButton from '../../../../components/UI/IconButton';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';

const OneTimeBenefitsTab = ({ year }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Fetch benefits
  const { data: benefits = [], isLoading, refetch } = useOneTimeBenefits({ year });
  
  // Mutations
  const createBenefit = useCreateOneTimeBenefit();
  const updateBenefit = useUpdateOneTimeBenefit();
  const deleteBenefit = useDeleteOneTimeBenefit();
  
  // Filter benefits
  const filteredBenefits = useMemo(() => {
    return benefits.filter((benefit) => {
      const matchesSearch = !searchTerm || 
        benefit.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        benefit.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        benefit.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = !filterType || benefit.benefit_type === filterType;
      
      return matchesSearch && matchesType;
    });
  }, [benefits, searchTerm, filterType]);
  
  // Calculate totals
  const totals = useMemo(() => {
    return filteredBenefits.reduce((acc, b) => ({
      originalValue: acc.originalValue + parseFloat(b.total_value || 0),
      taxableValue: acc.taxableValue + parseFloat(b.taxable_value || 0),
    }), { originalValue: 0, taxableValue: 0 });
  }, [filteredBenefits]);
  
  const handleOpenModal = (benefit = null) => {
    setEditingBenefit(benefit);
    setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBenefit(null);
  };
  
  const handleDelete = async (id) => {
    await deleteBenefit.mutateAsync(id);
    setDeleteConfirm(null);
  };
  
  const getBenefitTypeIcon = (type) => {
    switch (type) {
      case 'incentive_trip':
        return Plane;
      case 'long_service_award':
        return Award;
      default:
        return Gift;
    }
  };
  
  const getBenefitTypeLabel = (type) => {
    const option = BENEFIT_TYPE_OPTIONS.find(o => o.value === type);
    return option?.label || type;
  };
  
  const formatMoney = (value) =>
    `$${parseFloat(value || 0).toLocaleString('en-SG', { minimumFractionDigits: 2 })}`;
  
  // IRAS concession determination badge (taxable | exempt | partial)
  const renderTreatmentBadge = (benefit) => {
    const t = benefit.tax_treatment;
    if (!t) return null;
    const styles = {
      taxable: 'bg-red-100 text-red-800',
      exempt: 'bg-green-100 text-green-800',
      partial: 'bg-amber-100 text-amber-800',
    };
    const label = { taxable: 'Taxable', exempt: 'Exempt', partial: 'Partial' }[t] || t;
    return (
      <span
        className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${styles[t] || 'bg-gray-100 text-gray-700'}`}
        title={benefit.tax_treatment_reason || ''}
      >
        {label}
      </span>
    );
  };

  // Shared status badge (used by both the desktop table and mobile cards)
  const renderStatusBadge = (benefit) => (
    <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
      benefit.status === 'approved' 
        ? 'bg-green-100 text-green-800'
        : benefit.status === 'rejected'
        ? 'bg-red-100 text-red-800'
        : 'bg-yellow-100 text-yellow-800'
    }`}>
      {benefit.status || 'pending'}
    </span>
  );
  
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex justify-between animate-pulse">
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded w-48"></div>
              <div className="h-4 bg-gray-200 rounded w-64"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded-full w-32"></div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
              <div className="flex items-center">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                <div className="ml-2 sm:ml-4 space-y-2 min-w-0">
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-28"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              One-Time Benefits ({year})
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              Company trips, long service awards, and other d4 benefits
            </p>
          </div>
          
          <IconButton
            icon={Plus}
            onClick={() => handleOpenModal()}
            title="Add Benefit"
            ariaLabel="Add one-time benefit"
            variant="primary"
            className="flex-shrink-0"
          />
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{filteredBenefits.length}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Total Benefits</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                ${totals.originalValue.toLocaleString('en-SG', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Total Value</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                ${totals.taxableValue.toLocaleString('en-SG', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Taxable Value (d4)</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search by employee or description..."
            className="max-w-xl"
          />

          {/* Filter button */}
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={filterType ? 1 : 0}
            onClick={() => setShowFilters(v => !v)}
          />

          {/* Refresh */}
          <IconButton icon={RefreshCw} onClick={() => refetch()} title="Refresh" ariaLabel="Refresh benefits data" variant="default" />
        </div>

        {/* Collapsible filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label-admin">Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="select-admin w-full"
                >
                  <option value="">All Types</option>
                  {BENEFIT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Benefits List */}
      {filteredBenefits.length === 0 ? (
        /* Shared empty state */
        <div className="bg-white rounded-2xl shadow-sm border px-6 py-12 text-center text-gray-500">
          <Gift className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No benefits found for {year}</p>
        </div>
      ) : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {filteredBenefits.map((benefit) => (
              <div key={benefit.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {benefit.employee_name || benefit.staff_id}
                    </p>
                    <p className="text-xs text-gray-500">{benefit.staff_id}</p>
                  </div>
                  <div className="flex-shrink-0">{renderStatusBadge(benefit)}</div>
                </div>
                <div className="mt-3 space-y-2 flex-1">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Type</span>
                    <span className="text-gray-900 text-right truncate min-w-0">
                      {getBenefitTypeLabel(benefit.benefit_type)}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Description</span>
                    <span className="text-gray-900 text-right truncate min-w-0">
                      {benefit.description || '-'}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Date</span>
                    <span className="text-gray-900 text-right truncate min-w-0">
                      {benefit.benefit_date ? new Date(benefit.benefit_date).toLocaleDateString('en-SG') : '-'}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Total</span>
                    <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">
                      {formatMoney(benefit.total_value)}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Taxable</span>
                    <span className="text-gray-900 text-right truncate min-w-0 tabular-nums font-semibold">
                      {formatMoney(benefit.taxable_value)}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">IRAS Treatment</span>
                    <span className="text-right min-w-0">{renderTreatmentBadge(benefit)}</span>
                  </div>
                  {benefit.tax_treatment_reason && (
                    <p className="text-xs text-gray-500">{benefit.tax_treatment_reason}</p>
                  )}
                </div>
                <div className="card-action-footer">
                  <button
                    onClick={() => handleOpenModal(benefit)}
                    className="icon-button-admin"
                    title="Edit"
                    aria-label={`Edit benefit for ${benefit.employee_name || benefit.staff_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(benefit.id)}
                    className="icon-button-danger"
                    title="Delete"
                    aria-label={`Delete benefit for ${benefit.employee_name || benefit.staff_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop: full table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Employee
                    </th>
                    <th className="table-header-cell">
                      Type
                    </th>
                    <th className="table-header-cell">
                      Description
                    </th>
                    <th className="table-header-cell">
                      Date
                    </th>
                    <th className="table-header-cell text-right">
                      Total
                    </th>
                    <th className="table-header-cell text-right">
                      Taxable
                    </th>
                    <th className="table-header-cell text-center">
                      IRAS Treatment
                    </th>
                    <th className="table-header-cell text-center">
                      Status
                    </th>
                    <th className="table-header-cell text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBenefits.map((benefit) => {
                    const TypeIcon = getBenefitTypeIcon(benefit.benefit_type);
                    return (
                      <tr key={benefit.id} className="table-row-dense">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {benefit.employee_name || benefit.staff_id}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{benefit.staff_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <TypeIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {getBenefitTypeLabel(benefit.benefit_type)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900 truncate max-w-xs">
                            {benefit.description || '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {benefit.benefit_date ? new Date(benefit.benefit_date).toLocaleDateString('en-SG') : '-'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm text-gray-900">
                            {formatMoney(benefit.total_value)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-medium text-gray-900">
                            {formatMoney(benefit.taxable_value)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {renderTreatmentBadge(benefit)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {renderStatusBadge(benefit)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleOpenModal(benefit)}
                              className="icon-button-admin"
                              title="Edit"
                              aria-label={`Edit benefit for ${benefit.employee_name || benefit.staff_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(benefit.id)}
                              className="icon-button-danger"
                              title="Delete"
                              aria-label={`Delete benefit for ${benefit.employee_name || benefit.staff_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      
      {/* Add/Edit Modal */}
      {showModal && (
        <BenefitFormModal
          benefit={editingBenefit}
          year={year}
          onClose={handleCloseModal}
          onCreate={createBenefit}
          onUpdate={updateBenefit}
        />
      )}
      
      {/* Delete Confirmation */}
      {deleteConfirm && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Benefit?
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                This action cannot be undone. The benefit record will be permanently deleted.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn btn-admin-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleteBenefit.isPending}
                  className="btn btn-admin-primary"
                >
                  {deleteBenefit.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

// Benefit Form Modal Component
const BenefitFormModal = ({ benefit, year, onClose, onCreate, onUpdate }) => {
  const [formData, setFormData] = useState({
    staff_id: benefit?.staff_id || '',
    benefit_type: benefit?.benefit_type || 'gift',
    description: benefit?.description || '',
    benefit_date: benefit?.benefit_date || '',
    cash_value: benefit?.cash_value ?? '',
    non_cash_value: benefit?.non_cash_value ?? '',
    available_to_all_staff: benefit?.available_to_all_staff ?? false,
    tax_treatment_override: benefit?.tax_treatment_override || 'auto',
    notes: benefit?.notes || '',
  });

  const isEditing = !!benefit;
  const showAllStaff = ALL_STAFF_CONDITIONAL_TYPES.includes(formData.benefit_type);
  const formatMoney = (value) =>
    `$${parseFloat(value || 0).toLocaleString('en-SG', { minimumFractionDigits: 2 })}`;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      ...formData,
      cash_value: parseFloat(formData.cash_value) || 0,
      non_cash_value: parseFloat(formData.non_cash_value) || 0,
    };

    if (isEditing) {
      await onUpdate.mutateAsync({ id: benefit.id, data });
    } else {
      await onCreate.mutateAsync(data);
    }

    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate min-w-0">
              {isEditing ? 'Edit Benefit' : 'Add One-Time Benefit'}
            </h2>
            <button
              onClick={onClose}
              className="icon-button-admin flex-shrink-0"
              title="Close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Staff ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Staff ID *
              </label>
              <input
                type="text"
                value={formData.staff_id}
                onChange={(e) => handleChange('staff_id', e.target.value)}
                required
                className="input-admin w-full"
                placeholder="e.g., S0001"
              />
            </div>

            {/* Benefit Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Benefit Type *
              </label>
              <select
                value={formData.benefit_type}
                onChange={(e) => handleChange('benefit_type', e.target.value)}
                required
                className="select-admin w-full"
              >
                {BENEFIT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="input-admin w-full"
                placeholder="e.g., Festive hamper / 2025 Bali Incentive Trip"
              />
            </div>

            {/* Benefit Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Benefit Date *
              </label>
              <input
                type="date"
                value={formData.benefit_date}
                onChange={(e) => handleChange('benefit_date', e.target.value)}
                required
                className="input-admin w-full"
              />
            </div>

            {/* Cash / Non-cash values */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cash Value ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cash_value}
                  onChange={(e) => handleChange('cash_value', e.target.value)}
                  className="input-admin w-full"
                  placeholder="0.00"
                />
                <p className="mt-1 text-xs text-gray-500">Always taxable per IRAS.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Non-Cash Value ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.non_cash_value}
                  onChange={(e) => handleChange('non_cash_value', e.target.value)}
                  className="input-admin w-full"
                  placeholder="0.00"
                />
                <p className="mt-1 text-xs text-gray-500">Subject to concession rules.</p>
              </div>
            </div>

            {/* Available to all staff (conditional concessions) */}
            {showAllStaff && (
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.available_to_all_staff}
                  onChange={(e) => handleChange('available_to_all_staff', e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Available to all staff
                  <span className="block text-xs text-gray-500">
                    Required for this benefit's IRAS exemption to apply. If unchecked, it is fully taxable.
                  </span>
                </span>
              </label>
            )}

            {/* Tax treatment override */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tax Treatment
              </label>
              <select
                value={formData.tax_treatment_override}
                onChange={(e) => handleChange('tax_treatment_override', e.target.value)}
                className="select-admin w-full"
              >
                <option value="auto">Auto (apply IRAS concession rules)</option>
                <option value="taxable">Force Taxable</option>
                <option value="exempt">Force Exempt (non-cash only)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Taxability is determined automatically from IRAS concession rules; override only when you have a documented reason.
              </p>
            </div>

            {/* Current determination (when editing) */}
            {isEditing && benefit.tax_treatment && (
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm">
                <p className="text-gray-700">
                  <span className="font-medium">Computed:</span>{' '}
                  Total {formatMoney(benefit.total_value)} → Taxable{' '}
                  <span className="font-semibold">{formatMoney(benefit.taxable_value)}</span>
                </p>
                {benefit.tax_treatment_reason && (
                  <p className="mt-1 text-xs text-gray-500">{benefit.tax_treatment_reason}</p>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                className="input-admin w-full"
                placeholder="Additional notes..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button type="button" onClick={onClose} className="btn btn-admin-outline">
                Cancel
              </button>
              <button
                type="submit"
                disabled={onCreate.isPending || onUpdate.isPending}
                className="btn btn-admin-primary"
              >
                {(onCreate.isPending || onUpdate.isPending) ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isEditing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
};

export default OneTimeBenefitsTab;
